import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { keycloakAuthApiRef } from '../../apis';
import { useRegistryAccess } from '../portal-access/useRegistryAccess';
import { ColumnIn, ConnectionOption, ConnectionTables, OmSourceTable, ManifestTicketRef, RegistryApiError, TablePrTicket, lhRegistryApiRef } from './api';
import { Seg, Step, TICKET } from './ui';

// 레지스트리 source_type 기존 표기 (dev 실측: MySQL·PostgreSQL·Oracle)
/** 선택·등록 여부·제출의 식별자 — 같은 이름이 schema 마다 있을 수 있어 schema.table 로 잡는다. schema 가 없으면 bare */
const keyOf = (t: { schema: string; name: string }) => (t.schema ? `${t.schema}.${t.name}` : t.name);

const SOURCE_TYPE_OF: Record<string, string> = { Mysql: 'MySQL', Postgres: 'PostgreSQL', Oracle: 'Oracle' };

/** 줄마다 `이름 [타입]` — 타입은 이름 뒤 나머지 전부 (예: `price decimal(10,2)`) */
const parseColumns = (text: string): ColumnIn[] =>
  text.split('\n').map(l => l.trim()).filter(Boolean).map(l => {
    const [columnName, ...rest] = l.split(/\s+/);
    return rest.length ? { columnName, dataType: rest.join(' ') } : { columnName };
  });

/** 등록 직후 결과 패널용 */
type Created = { ids: number[]; skipped?: string; ticketKey: string };
const SKIP_REASON: Record<string, string> = { TICKET_IN_FLIGHT: '이 티켓은 이미 PR 진행 중이라', DOMAIN_MISMATCH: '이 티켓은 다른 domain 으로 PR 대기 중이라' };

/**
 * 신규 테이블 등록 탭: 1 domain 선택 → 2 원천 테이블·컬럼 → 3 근거. domain·connection 등록은 각자 화면이다(DPD-14023).
 * 2단계는 등록된 connection 을 고르고 OM 에 ingest 된 테이블 목록에서 고른다(비밀번호 불필요, 직접 입력 없음).
 * 테이블을 여러 개 고르면 한 티켓으로 묶는 batch 로 보낸다.
 */
export const RegistryNewForm = () => {
  const api = useApi(lhRegistryApiRef);
  const keycloak = useApi(keycloakAuthApiRef);
  const omUiUrl = useApi(configApiRef).getOptionalString('app.openMetadataUiUrl')?.replace(/\/+$/, '');
  const { canWrite: isAdmin } = useRegistryAccess();
  const navigate = useNavigate();
  const toList = () => navigate('..', { relative: 'path' });
  const [domains, setDomains] = useState<string[]>([]);
  const [counts, setCounts] = useState<Record<string, { n: number; y: number }>>({});
  const [dq, setDq] = useState('');
  const [domain, setDomain] = useState('');
  const [f, setF] = useState({ collectYn: 'Y' as 'Y' | 'N', note: '', columns: '' });
  const [ticketKey, setTicketKey] = useState('');
  const [reason, setReason] = useState('');
  const [ticketStatus, setTicketStatus] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ code: string; message: string }>();
  const [created, setCreated] = useState<Created>();
  const [bot, setBot] = useState<TablePrTicket>();
  const [conns, setConns] = useState<ConnectionOption[]>([]);
  const [connWarn, setConnWarn] = useState<string>();
  const [conn, setConn] = useState('');
  const [omTables, setOmTables] = useState<ConnectionTables>();
  const [loadingTables, setLoadingTables] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [detail, setDetail] = useState<OmSourceTable>();
  const [registered, setRegistered] = useState<Set<string>>(new Set());
  // legacy 에만 있는 테이블은 막지 않는다 — 신규 행과 같은 키로 공존할 수 있다. 표시만 한다
  // 설계 §5.8: 같은 (domain, table_name) 의 legacy 행 — 원천 키(source_system·source_table_name)가 같은 것도 함께 본다
  const [legacySet, setLegacySet] = useState<Set<string>>(new Set());
  const [legacyNames, setLegacyNames] = useState<Set<string>>(new Set());
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF(p => ({ ...p, [k]: e.target.value }));

  // 토큰 발급 실패는 막지 않는다 — 권한 판단은 서버에 있다(user 모드면 401 OM_TOKEN_REQUIRED)
  const omToken = async () => {
    try {
      return (await keycloak.getAccessToken()) || undefined;
    } catch {
      return undefined;
    }
  };

  useEffect(() => {
    omToken().then(t => api.listConnections(t)).then(setConns).catch(e => setConnWarn(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  useEffect(() => {
    api.listDomains().then(d => setDomains(d.map(x => x.domain))).catch(e => setError({ code: '', message: e.message }));
    // domain grid 의 테이블 수·수집 Y 수는 목록에서 센다
    api.listTables({}).then(rows => {
      const key = (r: { sourceSystem: string; sourceTableName: string }) => `${r.sourceSystem}\u0000${r.sourceTableName}`.toLowerCase();
      setRegistered(new Set(rows.filter(r => !r.legacy).map(key)));
      setLegacySet(new Set(rows.filter(r => r.legacy).map(key)));
      setLegacyNames(new Set(rows.filter(r => r.legacy).map(r => `${r.domain}\u0000${r.tableName}`.toLowerCase())));
      const c: Record<string, { n: number; y: number }> = {};
      for (const r of rows) {
        c[r.domain] = c[r.domain] ?? { n: 0, y: 0 };
        c[r.domain].n++;
        if (r.collectYn === 'Y') c[r.domain].y++;
      }
      setCounts(c);
    }).catch(() => undefined);
  }, [api]);

  // 형식이 맞는 티켓이면 Jira 상태를 미리 보여 준다 (실재 확인 자체는 저장 시 서버가 한다)
  useEffect(() => {
    setTicketStatus(undefined);
    if (!TICKET.test(ticketKey)) return undefined;
    const t = setTimeout(() => api.ticketStatuses([ticketKey]).then(s => setTicketStatus(s[ticketKey]?.status)), 400);
    return () => clearTimeout(t);
  }, [api, ticketKey]);

  // BOT PR 탐지는 등록 응답 뒤 비동기로 돈다 — 잠시 뒤 한 번 확인해 연결됐으면 보여 준다 (없으면 조용히 넘어간다)
  useEffect(() => {
    if (!created) return undefined;
    const t = setTimeout(() => api.tablePrs(created.ids[0]).then(r => setBot(r.tickets.find(x => x.ticket_key === created.ticketKey && x.bot_pr_number))).catch(() => undefined), 1500);
    return () => clearTimeout(t);
  }, [api, created]);

  const pickedTables = (omTables?.tables ?? []).filter(t => picked.includes(keyOf(t)));
  const names = pickedTables.map(t => t.name);
  // table_name 은 domain 안에서 유일하다(uq_lh_ct_target) — 다른 schema 의 같은 이름은 한 번에 하나만 등록한다
  const dupNames = names.filter((n, i) => names.indexOf(n) !== i);
  const sel = conns.find(c => c.serviceName === conn);
  const sourceSystem = sel?.sourceSystem ?? '';
  const sourceType = (sel && SOURCE_TYPE_OF[sel.serviceType]) ?? '';
  const adds = parseColumns(f.columns);
  const ticketBad = !!ticketKey && !TICKET.test(ticketKey);
  let ticketCheck = ticketStatus ? `✓ ${ticketKey} · ${ticketStatus}` : `저장 시 Jira에서 ${ticketKey} 실재 여부를 확인합니다`;
  if (ticketBad) ticketCheck = '티켓 키 형식이 아닙니다 — 예: DPD-13791';
  const missing = [
    !domain && 'domain',
    !conn && 'connection',
    !names.length && '테이블 선택',
    !TICKET.test(ticketKey) && '근거 티켓',
    !reason.trim() && '등록 사유',
  ].filter((x): x is string => !!x);
  const canSave = isAdmin && !busy && !missing.length && !dupNames.length;
  const shownDomains = domains.filter(d => d.toLowerCase().includes(dq.trim().toLowerCase()));

  // 기존 행의 source_table_name 은 bare 일 수 있다(schema 를 알 수 없음) — bare 일치도 등록된 것으로 보아 중복 등록을 막는다
  const isRegistered = (t: { schema: string; name: string }) =>
    registered.has(`${sourceSystem}\u0000${keyOf(t)}`.toLowerCase()) || registered.has(`${sourceSystem}\u0000${t.name}`.toLowerCase());

  const inLegacy = (t: { schema: string; name: string }) =>
    legacyNames.has(`${domain}\u0000${t.name}`.toLowerCase()) ||
    legacySet.has(`${sourceSystem}\u0000${keyOf(t)}`.toLowerCase()) || legacySet.has(`${sourceSystem}\u0000${t.name}`.toLowerCase());

  const pickConn = async (name: string) => {
    setConn(name);
    setPicked([]);
    setOmTables(undefined);
    const c = conns.find(x => x.serviceName === name);
    if (!c) return;
    setLoadingTables(true);
    try {
      setOmTables(await api.connectionTables(name, await omToken()));
    } catch (e) {
      setError({ code: '', message: (e as Error).message });
    } finally {
      setLoadingTables(false);
    }
  };

  const create = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const base = { domain, sourceSystem, sourceType, collectYn: f.collectYn, note: f.note || undefined };
      const columns = adds.length ? adds : undefined;
      // 여러 줄이면 한 요청·한 티켓으로 묶는 batch (테이블마다 version 1 + 이력 1건)
      const made: { id: number; mt?: ManifestTicketRef }[] =
        names.length > 1
          ? (await api.batch({ ticketKey, reason, items: pickedTables.map(t => ({ op: 'create' as const, ...base, tableName: t.name, sourceTableName: keyOf(t), columns })) })).items.map(x => ({ id: x.table.id, mt: x.manifestTicket }))
          : [await api.createTable({ ...base, tableName: names[0], sourceTableName: keyOf(pickedTables[0]), columns, reason, ticketKey })].map(x => ({ id: x.id, mt: x.manifestTicket }));
      setCreated({ ids: made.map(m => m.id), skipped: made.find(m => m.mt?.skipped)?.mt?.skipped, ticketKey });
    } catch (e) {
      const err = e as RegistryApiError;
      setError({ code: err.code ?? '', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  if (created)
    return (
      <Paper variant="outlined">
        <Box p={3} display="flex" flexDirection="column" style={{ gap: 8 }}>
          <Alert severity="success">{created.ids.length}개 테이블을 등록했습니다 ({created.ticketKey}).</Alert>
          {created.skipped
            ? <Alert severity="warning">{SKIP_REASON[created.skipped] ?? '이 티켓은'} manifest PR 대기열에는 올리지 않았습니다 (등록은 완료됐습니다).</Alert>
            : <Alert severity="info">다음 단계: 관리자가 'Manifest 검토' 탭에서 domain 단위로 묶어 PR 을 만듭니다. 상태는 목록의 근거 티켓 칸에서 볼 수 있습니다.</Alert>}
          {bot && <Alert severity="info">BOT PR #{bot.bot_pr_number} 연결됨 ({bot.bot_pr_state})</Alert>}
          <Box><Button variant="contained" color="primary" onClick={toList}>목록으로</Button></Box>
        </Box>
      </Paper>
    );

  const field = { variant: 'outlined' as const, size: 'small' as const };
  return (
    <Paper variant="outlined" component="section" aria-label="신규 테이블 등록">
      <Box p={3} display="flex" flexDirection="column" style={{ gap: 16 }}>
        <Box>
          <Typography variant="h6" component="h2">신규 테이블 등록</Typography>
          <Typography variant="body2" color="textSecondary">
            domain 은 grid 에서 고릅니다 (없으면 '신규 domain' 탭에서 먼저 추가). 근거 티켓과 사유를 함께 남깁니다.
          </Typography>
        </Box>
        {!isAdmin && <Alert severity="info"><b>읽기 전용</b> — 쓰기 권한이 없어 등록할 수 없습니다.</Alert>}
        {error?.code === 'JIRA_UNAVAILABLE' && <Alert severity="error">Jira에 연결할 수 없어 등록을 막았습니다 (503 JIRA_UNAVAILABLE). 잠시 후 다시 시도하세요.</Alert>}
        {error && error.code !== 'JIRA_UNAVAILABLE' && <Alert severity="error">{error.message}</Alert>}

        <fieldset disabled={!isAdmin} style={{ border: 0, margin: 0, padding: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Step
            n={1} title="domain" sub={`lh_domain ${domains.length}개`}
            action={<TextField id="rn-1" {...field} type="search" placeholder="domain 검색" inputProps={{ 'aria-label': 'domain 검색' }} value={dq} onChange={e => setDq(e.target.value)} />}
          >
            <TableContainer style={{ maxHeight: 260 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    {['domain', '테이블', '수집 Y'].map(h => <TableCell key={h}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shownDomains.map(d => {
                    const on = domain === d;
                    return (
                      <TableRow key={d} hover selected={on} style={{ cursor: 'pointer' }} onClick={() => setDomain(d)}>
                        <TableCell padding="checkbox"><Radio size="small" checked={on} inputProps={{ 'aria-label': `${d} 선택` }} /></TableCell>
                        <TableCell>{d}</TableCell>
                        <TableCell>{counts[d]?.n ?? 0}</TableCell>
                        <TableCell>{counts[d]?.y ?? 0}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {!shownDomains.length && (
                <Box p={2}><Typography variant="body2" color="textSecondary">"{dq}"와 맞는 domain이 없습니다 — '신규 domain' 탭에서 추가하세요.</Typography></Box>
              )}
            </TableContainer>
            <Typography variant="body2">선택: <b>{domain || '—'}</b></Typography>
          </Step>

          <Step n={2} title="원천 테이블 · 컬럼" sub={conn ? `connection → OpenMetadata(${conn})` : 'OM ingest 된 테이블에서 선택'}>
            <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
              <TextField id="rn-c" {...field} select SelectProps={{ native: true }} label="등록된 connection" value={conn} onChange={e => pickConn(e.target.value)} InputLabelProps={{ shrink: true }} style={{ minWidth: 260 }}>
                <option value="">선택</option>
                {conns.map(c => <option key={c.serviceName} value={c.serviceName}>{`${c.serviceName} (${c.serviceType})`}</option>)}
              </TextField>
              {sel && <Typography variant="body2">등록될 source_system: <b>{sel.sourceSystem}</b> · 유형: <b>{sourceType || '—'}</b></Typography>}
              {!conn && <Typography variant="caption" color="textSecondary">없으면 '신규 connection' 탭에서 먼저 등록하고 OM 에서 ingest 합니다.</Typography>}
            </Box>
            {connWarn && <Alert severity="warning">connection 목록을 불러오지 못했습니다. ({connWarn})</Alert>}
            {loadingTables && <Typography variant="caption">테이블 목록을 불러오는 중…</Typography>}
            {omTables && !omTables.ingested && (
              <Alert severity="info">
                {isAdmin ? '이 서비스는 OM ingest 전입니다 — OM 에 ingestion 을 등록한 뒤 테이블을 고를 수 있습니다.' : 'OM 에 ingest 된 뒤 등록할 수 있습니다 — admin 에게 요청하세요.'}
                {isAdmin && omUiUrl && (
                  <>
                    {' '}
                    <a href={`${omUiUrl}/service/databaseServices/${encodeURIComponent(conn)}/add-ingestion/metadata`} target="_blank" rel="noopener noreferrer">OM ingestion 등록</a>
                  </>
                )}
              </Alert>
            )}
            {omTables?.ingested && (
              <TableContainer style={{ maxHeight: 240 }}>
                <Table size="small" stickyHeader>
                  <TableHead><TableRow><TableCell padding="checkbox" />{['schema', '테이블', '컬럼 수', ''].map(h => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>
                    {omTables.tables.map(t => {
                      const done = isRegistered(t);
                      const k = keyOf(t);
                      return (
                        <TableRow key={k} hover style={{ cursor: 'pointer' }} onClick={() => setDetail(t)}>
                          <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                            <Checkbox size="small" disabled={done} checked={picked.includes(k)} inputProps={{ 'aria-label': `${k} 선택` }}
                              onChange={e => setPicked(p => (e.target.checked ? [...p, k] : p.filter(x => x !== k)))} />
                          </TableCell>
                          <TableCell>{t.schema}</TableCell><TableCell>{t.name}</TableCell><TableCell>{t.columnCount}</TableCell>
                          <TableCell>
                            {done && <Typography variant="caption" color="textSecondary">이미 등록됨</Typography>}
                            {!done && inLegacy(t) && <Typography variant="caption" color="textSecondary" title="legacy 적재 행에 같은 테이블이 있습니다 — 등록은 새 행으로 만들어집니다">legacy 에 있음</Typography>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {picked.length > 0 && <Typography variant="caption">선택한 {picked.length}개가 등록됩니다 — table_name 은 테이블 이름, 원천 테이블은 schema.테이블 입니다.</Typography>}
            {dupNames.length > 0 && <Alert severity="warning">같은 이름({[...new Set(dupNames)].join(', ')})의 테이블을 여러 schema 에서 골랐습니다 — table_name 은 domain 안에서 유일해 하나만 등록할 수 있습니다.</Alert>}
            <Dialog open={!!detail} onClose={() => setDetail(undefined)} maxWidth="md" fullWidth aria-label="컬럼 목록">
              <DialogTitle>{detail ? `${keyOf(detail)} — 컬럼 ${detail.columns.length}개` : ''}</DialogTitle>
              <DialogContent dividers>
                {detail && !detail.columns.length && <Typography variant="body2" color="textSecondary">OM 에 컬럼 정보가 없습니다.</Typography>}
                {detail && detail.columns.length > 0 && (
                  <Table size="small" stickyHeader>
                    <TableHead><TableRow>{['name', 'dataType', 'constraint', 'description'].map(h => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                      {detail.columns.map(c => (
                        <TableRow key={c.name}><TableCell>{c.name}</TableCell><TableCell>{c.dataType}</TableCell><TableCell>{c.constraint ?? ''}</TableCell><TableCell>{c.description ?? ''}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DialogContent>
              <DialogActions><Button onClick={() => setDetail(undefined)}>닫기</Button></DialogActions>
            </Dialog>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Typography variant="body2" color="textSecondary">수집 여부</Typography>
              <Seg value={f.collectYn} options={[['Y', 'Y · 수집'], ['N', 'N · 미수집']]} onChange={v => setF(p => ({ ...p, collectYn: v }))} disabled={!isAdmin} label="수집 여부" />
            </Box>
            <TextField id="rn-8" {...field} label="비고" value={f.note} onChange={set('note')} />
            <TextField id="rn-9" {...field} label="컬럼 (줄마다 `이름 타입`)" value={f.columns} onChange={set('columns')} multiline minRows={3} helperText={adds.length ? `컬럼 ${adds.length}개` : undefined} />
          </Step>

          <Step n={3} title="근거" sub="등록하는 테이블 전체에 같은 값이 들어갑니다">
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              <TextField id="rn-10" {...field} label="근거 티켓 (필수)" placeholder="DPD-13791" value={ticketKey} onChange={e => setTicketKey(e.target.value.trim())} error={ticketBad} />
              <TextField id="rn-11" {...field} label="등록 사유 (필수)" placeholder="예: add_table 배포 완료" value={reason} onChange={e => setReason(e.target.value)} />
            </Box>
            {ticketKey && (
              <Typography variant="caption" color={ticketBad ? 'error' : 'textSecondary'}>
                {ticketCheck}
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary">
              등록이 끝나면 이 티켓은 manifest PR 대기열(PR 대기)에 연결됩니다. 티켓이 이미 진행 중이거나 다른 domain 이면 연결만 건너뜁니다 — 등록은 그대로 성공합니다.
            </Typography>
          </Step>
        </fieldset>

        <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 'auto' }}>{missing.length ? `남은 입력: ${missing.join(', ')}` : ''}</Typography>
          <Button variant="outlined" onClick={toList}>취소</Button>
          <Button variant="contained" color="primary" onClick={create} disabled={!canSave}>{names.length > 1 ? `${names.length}개 테이블 등록` : '등록'}</Button>
        </Box>
        <details>
          <summary><Typography variant="body2" component="span">저장 시 서버가 하는 일</Typography></summary>
          <Typography variant="body2" component="ol">
            <li>로그인 계정의 쓰기 권한 확인 (없으면 403)</li>
            <li>입력 형식 검사 (공백·티켓 키 형식)</li>
            <li>Jira에서 티켓 실재 확인 (없으면 400, 장애면 503)</li>
            <li>테이블마다 본표 version 1 + 이력 행을 저장</li>
            <li>작성자는 서버가 인증 정보에서 채움 — 입력받지 않음</li>
            <li>티켓을 manifest PR 대기열에 연결 — 연결할 수 없으면 연결만 생략(등록은 성공)</li>
          </Typography>
        </details>
      </Box>
    </Paper>
  );
};
